import { z } from "zod";
import { ManualMedicationStatus, ManualVitalsMetric } from "./api/signals.js";
import { ActivationPredicateSchema } from "./personaActivation.js";
/**
 * Capture prompts.
 *
 * A capture prompt is a proactive "log this now" CTA that Heartbeat surfaces on
 * the Today surface, sitting below microsteps on the lightest-action ladder. It
 * points at one of the nine manual-log forms behind the web "Add to today"
 * button, pre-selected to a specific field, so a member logs their dose, blood
 * pressure, or a meal without hunting for the button.
 *
 * Templates are config, not code. Each carries an activation predicate reused
 * from the persona-activation DSL (evaluateActivation), so eligibility is
 * expressed as data: who qualifies (medication_in_class / condition_present),
 * when (during_window), whether it is redundant (not + signal_active_recently),
 * and how often (surface_count_today / family_count_in_window /
 * last_dismissed_minutes_ago). Copy is static and reviewed here; the model only
 * selects a candidate by id, it never writes the copy.
 */
// The nine manual-log actions the "Add to today" surface exposes. Mirrors the
// `type` discriminant on RecordManualSignalEntryRequest in ./api/signals.ts.
export const CaptureLogActionSchema = z.enum([
    "food",
    "hydration",
    "sleep",
    "mood",
    "symptoms",
    "caffeine",
    "workout",
    "medication",
    "vitals",
]);
/**
 * Which specific sub-form to open, so the tap lands on the exact field. Every
 * field is optional; a template sets only what applies to its logAction (a
 * vitals prompt sets vitalsMetric, a medication prompt sets medicationStatus, a
 * food prompt may set mealType, though food's meal-type is normally derived from
 * the member's local time at surface time).
 */
export const CapturePreselectSchema = z
    .object({
    vitalsMetric: ManualVitalsMetric.optional(),
    medicationStatus: ManualMedicationStatus.optional(),
    mealType: z.string().min(1).max(40).optional(),
})
    .strict();
export const CapturePromptTemplateSchema = z.object({
    /** Stable id, referenced verbatim by a Heartbeat decision's capturePromptId. */
    id: z.string().min(1).max(60),
    /** Which of the nine manual-log forms this prompt opens. */
    logAction: CaptureLogActionSchema,
    /** Preselect target so the tap opens the exact sub-form. */
    preselect: CapturePreselectSchema.optional(),
    /**
     * Family slug for per-family cooldown, e.g. "capture.vitals.bp". Used by the
     * family_count_in_window / last_dismissed_minutes_ago frequency predicates.
     */
    family: z.string().min(1).max(60),
    /** Reviewed, static card copy. Filled onto the widget deterministically. */
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(280),
    /** Higher wins when several templates are eligible on the same tick. */
    priority: z.number().int(),
    /** Eligibility predicate, evaluated by evaluateActivation(). */
    activation: ActivationPredicateSchema,
});
/**
 * A template that passed its predicate this tick, resolved into what the agent
 * sees and what a client renders. `title`/`body` are the template's static copy
 * (food's meal-type may adjust the preselect before this is built). The agent
 * picks one of these by `id`; the pipeline rebuilds the card from the matching
 * template so the model never authors capture copy.
 */
export const CaptureCandidateSchema = z.object({
    id: z.string(),
    logAction: CaptureLogActionSchema,
    preselect: CapturePreselectSchema.optional(),
    title: z.string(),
    body: z.string(),
    family: z.string(),
});
//# sourceMappingURL=capturePrompt.js.map